import React from "react";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Users,
  GraduationCap,
  Briefcase,
  DollarSign,
  Heart,
  MessageCircle,
  Facebook,
  Instagram,
  Twitter,
  Smartphone,
  CheckCircle,
  XCircle,
  Star,
  Award,
  Globe,
  Target,
} from "lucide-react";

const ViewSubscribersModal = ({ isOpen, onClose, subscribers }) => {
  if (!isOpen || !subscribers) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "Not verified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status) => {
    return status === 1 ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500" />
    );
  };

  const getStatusText = (status) => {
    return status === 1 ? "Active" : "Inactive";
  };

  const getStatusColor = (status) => {
    return status === 1
      ? "text-green-700 bg-green-50 border-green-200"
      : "text-red-700 bg-red-50 border-red-200";
  };

  const getChannelIcon = (channel) => {
    return channel === "web" ? (
      <Smartphone className="w-4 h-4 text-cyan-600" />
    ) : (
      <MessageCircle className="w-4 h-4 text-teal-600" />
    );
  };

  const getChannelColor = (channel) => {
    return channel === "web"
      ? "text-cyan-700 bg-cyan-50 border-cyan-200"
      : "text-teal-700 bg-teal-50 border-teal-200";
  };

  const renderField = (
    label,
    value,
    icon,
    fallback = "Not provided",
    color = "blue"
  ) => {
    const colorClasses = {
      blue: "bg-blue-50 border-blue-100 text-blue-700",
      green: "bg-green-50 border-green-100 text-green-700",
      purple: "bg-purple-50 border-purple-100 text-purple-700",
      orange: "bg-orange-50 border-orange-100 text-orange-700",
      red: "bg-red-50 border-red-100 text-red-700",
      pink: "bg-pink-50 border-pink-100 text-pink-700",
      indigo: "bg-indigo-50 border-indigo-100 text-indigo-700",
      teal: "bg-teal-50 border-teal-100 text-teal-700",
    };

    return (
      <div className="group relative overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6C6C]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative flex items-start space-x-3 p-3">
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 border border-[#FF6C6C]/20`}
          >
            <div className="text-[#FF6C6C]">{icon}</div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
            <div
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium border ${
                value
                  ? colorClasses[color]
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
            >
              {value || fallback}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSocialField = (label, value, icon, platform) => {
    if (!value) return null;
    return (
      <div className="group relative overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6C6C]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative flex items-center space-x-3 p-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 border border-[#FF6C6C]/20">
            {icon}
          </div>
          <div className="flex-1">
            <span className="text-sm font-semibold text-gray-700">
              {platform}
            </span>
            <p className="text-sm text-gray-600 truncate">{value}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderStatsCard = (title, value, icon, color = "blue") => {
    const colorClasses = {
      blue: "from-blue-500 to-blue-600",
      green: "from-green-500 to-green-600",
      purple: "from-purple-500 to-purple-600",
      orange: "from-orange-500 to-orange-600",
      red: "from-red-500 to-red-600",
      pink: "from-pink-500 to-pink-600",
    };

    return (
      <div className="relative overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6C6C]/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-lg font-bold text-gray-900">{value}</p>
            </div>
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${colorClasses[color]} shadow-sm`}
            >
              {icon}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-900/75 backdrop-blur-sm"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Enhanced Header */}
          <div className="relative overflow-hidden bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] px-6 py-4">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-8 -translate-x-8"></div>

            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    Subscriber Profile
                  </h3>
                  <p className="text-white/90 text-sm">ID: #{subscribers.id}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/90 hover:text-white transition-colors p-2 rounded-xl hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
            {/* Enhanced Profile Section */}
            <div className="mb-6">
              <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 p-4">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#FF6C6C]/10 to-transparent rounded-full -translate-y-12 translate-x-12"></div>

                <div className="relative flex items-center space-x-4 mb-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
                      {subscribers.full_name?.charAt(0) || "U"}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full border-2 border-[#FF6C6C] flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-[#FF6C6C] rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {subscribers.full_name}
                    </h2>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                          subscribers.is_active
                        )}`}
                      >
                        {getStatusIcon(subscribers.is_active)}
                        <span className="ml-1">
                          {getStatusText(subscribers.is_active)}
                        </span>
                      </span>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getChannelColor(
                          subscribers.channel
                        )}`}
                      >
                        {getChannelIcon(subscribers.channel)}
                        <span className="ml-1 uppercase">
                          {subscribers.channel}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3">
                  {renderStatsCard(
                    "Verification",
                    subscribers.email_verified_at ? "✓" : "⏳",
                    <Shield className="w-4 h-4 text-white" />,
                    "green"
                  )}
                  {renderStatsCard(
                    "Channel",
                    subscribers.channel?.toUpperCase() || "N/A",
                    <Smartphone className="w-4 h-4 text-white" />,
                    "blue"
                  )}
                  {renderStatsCard(
                    "Categories",
                    subscribers.category?.length || 0,
                    <Target className="w-4 h-4 text-white" />,
                    "purple"
                  )}
                </div>
              </div>
            </div>

            {/* Combined Contact & Location */}
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center mr-2">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Contact & Location
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderField(
                  "Email",
                  subscribers.email,
                  <Mail className="w-4 h-4" />,
                  "No email",
                  "blue"
                )}
                {renderField(
                  "Phone",
                  subscribers.phone_number,
                  <Phone className="w-4 h-4" />,
                  "No phone",
                  "green"
                )}
                {renderField(
                  "Location",
                  subscribers.location,
                  <MapPin className="w-4 h-4" />,
                  "Unknown",
                  "purple"
                )}
                {renderField(
                  "Region",
                  subscribers.region,
                  <Globe className="w-4 h-4" />,
                  "Not specified",
                  "orange"
                )}
                {renderField(
                  "Email Verified",
                  formatDate(subscribers.email_verified_at),
                  <Shield className="w-4 h-4" />,
                  "Not verified",
                  "pink"
                )}
                {renderField(
                  "Phone Verified",
                  formatDate(subscribers.phone_number_verified_at),
                  <Shield className="w-4 h-4" />,
                  "Not verified",
                  "indigo"
                )}
              </div>
            </div>

            {/* Combined Personal & Professional */}
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center mr-2">
                  <User className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Personal & Professional
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderField(
                  "Gender",
                  subscribers.gender,
                  <User className="w-4 h-4" />,
                  "Not specified",
                  "purple"
                )}
                {renderField(
                  "Age",
                  subscribers.age,
                  <Calendar className="w-4 h-4" />,
                  "Not specified",
                  "blue"
                )}
                {renderField(
                  "Occupation",
                  subscribers.occupation,
                  <Briefcase className="w-4 h-4" />,
                  "Not specified",
                  "green"
                )}
                {renderField(
                  "Education",
                  subscribers.educational_level,
                  <GraduationCap className="w-4 h-4" />,
                  "Not specified",
                  "orange"
                )}
                {renderField(
                  "Income Level",
                  subscribers.income_level,
                  <DollarSign className="w-4 h-4" />,
                  "Not specified",
                  "pink"
                )}
                {renderField(
                  "Household Size",
                  subscribers.household_size,
                  <Users className="w-4 h-4" />,
                  "Not specified",
                  "indigo"
                )}
              </div>
            </div>

            {/* Preferences & Categories */}
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center mr-2">
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Preferences & Categories
                </h3>
              </div>
              <div className="space-y-4">
                {subscribers.category && subscribers.category.length > 0 ? (
                  <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 p-4">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-purple-200/50 to-pink-200/50 rounded-full -translate-y-8 translate-x-8"></div>
                    <div className="relative">
                      <div className="flex items-center mb-3">
                        <Target className="w-4 h-4 text-purple-600 mr-2" />
                        <p className="text-sm font-semibold text-gray-900">
                          Interested Categories
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {subscribers.category.map((cat, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200 shadow-sm"
                          >
                            <Star className="w-3 h-3 mr-1 text-purple-500" />
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  renderField(
                    "Interested Categories",
                    null,
                    <Target className="w-4 h-4" />,
                    "No categories selected",
                    "purple"
                  )
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {renderField(
                    "Communication",
                    subscribers.communication_channels,
                    <MessageCircle className="w-4 h-4" />,
                    "Not specified",
                    "blue"
                  )}
                  {renderField(
                    "Deal Subscription",
                    subscribers.deal_subscription,
                    <CheckCircle className="w-4 h-4" />,
                    "Not specified",
                    "green"
                  )}
                  {renderField(
                    "Privacy Policy",
                    subscribers.privacy_policy,
                    <Shield className="w-4 h-4" />,
                    "Not specified",
                    "orange"
                  )}
                </div>
              </div>
            </div>

            {/* Social Media - Only if exists */}
            {(subscribers.facebook ||
              subscribers.instagram ||
              subscribers.twitter ||
              subscribers.tiktok) && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center mr-2">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Social Media
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {renderSocialField(
                    "Facebook",
                    subscribers.facebook,
                    <Facebook className="w-4 h-4 text-blue-600" />,
                    "Facebook"
                  )}
                  {renderSocialField(
                    "Instagram",
                    subscribers.instagram,
                    <Instagram className="w-4 h-4 text-pink-600" />,
                    "Instagram"
                  )}
                  {renderSocialField(
                    "Twitter",
                    subscribers.twitter,
                    <Twitter className="w-4 h-4 text-blue-400" />,
                    "Twitter"
                  )}
                  {renderSocialField(
                    "TikTok",
                    subscribers.tiktok,
                    <Smartphone className="w-4 h-4 text-black" />,
                    "TikTok"
                  )}
                </div>
              </div>
            )}

            {/* Technical Information - Compact */}
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center mr-2">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Technical Info
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderField(
                  "User Slug",
                  subscribers.user_slug,
                  <Shield className="w-4 h-4" />,
                  "Not available",
                  "indigo"
                )}
                {renderField(
                  "Provider",
                  subscribers.provider,
                  <Shield className="w-4 h-4" />,
                  "Not specified",
                  "blue"
                )}
                {renderField(
                  "Provider ID",
                  subscribers.provider_id,
                  <Shield className="w-4 h-4" />,
                  "Not specified",
                  "green"
                )}
                {renderField(
                  "Data Sharing",
                  subscribers.data_sharing,
                  <Shield className="w-4 h-4" />,
                  "Not specified",
                  "orange"
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Footer */}
          <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center">
                  <Award className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-gray-600">Profile Complete</span>
              </div>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 font-semibold text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSubscribersModal;
